import axios, { AxiosError } from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../../constants';
import {
  CreditMemoAllocationCreate,
  DepositUsageIntentCreate,
} from '../../state/v1/DepositUsageIntent';
import { formatRefId } from '../../utils/referenceHelpers';
import { CustomerData } from './CreditAccountService';
import { getOrCreateCreditAccountId, putWithCreditAccountRetry } from './putWithCreditAccountRetry';

export interface DepositUsageIntentResponse {
  id: string;
  totalAmount: string;
  currency: string;
  autoAllocate: boolean;
  transactionRef: string;
  creditAccountRef: string;
}

export interface UpsertDepositUsageIntentParams {
  /** The credit account ID from R-Series (can be empty/0 if customer doesn't have one yet) */
  creditAccountId: string | number;
  /** The customer ID from R-Series (required if creditAccountId is empty/0) */
  customerId: string;
  /** The sale/transaction ID */
  saleId: string;
  /** The total amount across all credit memo allocations */
  totalAmount: number;
  /** The currency code (e.g., 'USD', 'CAD') */
  currency: string;
  /** The credit memo allocations selected by the user */
  creditMemoAllocations: CreditMemoAllocationCreate[];
  /** Customer data - required if credit account may not exist on Invoicing */
  customer?: CustomerData;
}

export default class DepositUsageIntentService {
  /**
   * Creates or updates a deposit usage intent for a transaction.
   * If the customer doesn't have a credit account on R-Series, it will create one with $0 limit.
   * If the credit account doesn't exist on Invoicing (404), it will create it and retry.
   *
   * @param params The parameters for upserting a deposit usage intent
   * @returns The deposit usage intent response with the credit account ID used
   */
  static async upsertDepositUsageIntent(
    params: UpsertDepositUsageIntentParams
  ): Promise<DepositUsageIntentResponse & { creditAccountId: string }> {
    const {
      creditAccountId,
      customerId,
      saleId,
      totalAmount,
      currency,
      creditMemoAllocations,
      customer,
    } = params;

    if (!saleId) {
      throw new Error(`Missing required parameter: saleId=${saleId}`);
    }

    const creditAccountIdStr = await getOrCreateCreditAccountId(creditAccountId, customerId);
    const creditAccountRef = formatRefId(creditAccountIdStr);
    const transactionRef = formatRefId(`${saleId}`);

    const payload: DepositUsageIntentCreate = {
      totalAmount: totalAmount.toString(),
      currency,
      autoAllocate: false,
      creditMemoAllocations,
    };

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/deposit-usage-intent`;

    return putWithCreditAccountRetry<DepositUsageIntentCreate, DepositUsageIntentResponse>({
      url,
      payload,
      creditAccountIdStr,
      creditAccountRef,
      transactionRef,
      currency,
      customer,
      operationName: 'deposit usage intent',
      datadogContext: { totalAmount },
    });
  }

  /**
   * Deletes a deposit usage intent for a transaction from Invoicing.
   * This is idempotent - returns successfully even if the deposit usage intent doesn't exist.
   *
   * @param creditAccountId The credit account ID from R-Series
   * @param saleId The sale/transaction ID
   */
  static async deleteDepositUsageIntent(
    creditAccountId: string | number,
    saleId: string
  ): Promise<void> {
    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0') {
      return;
    }

    if (!saleId) {
      return;
    }

    const creditAccountRef = formatRefId(`${creditAccountId}`);
    const transactionRef = formatRefId(`${saleId}`);

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/deposit-usage-intent`;

    try {
      await axios.delete(url);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        return;
      }

      datadogRum.addError(error, {
        creditAccountRef,
        transactionRef,
        message: `Failed to delete deposit usage intent for transactionRef: ${transactionRef}`,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
